import { memo } from 'react';
import {
  Button,
  Dropdown,
  Option,
  Table,
  TableBody,
  TableCell,
  TableHeader,
  TableHeaderCell,
  TableRow,
  Text,
} from '@coreai/fluentui-react';
import { useAppStore } from '@/stores/appStore';
import type { ReduceMotionPreference } from '@/stores/appStore';
import { getShortcutParts, type ShortcutId } from '@/utils/input';
import { useSettingCardStyles } from './sectionStyles';
import { useSettingsModalStyles } from '../SettingsModal.styles';

interface AccessibilitySectionProps {
  /** Opens the existing KeyboardShortcutsDialog. Lifted to the modal so
   *  the dialog mounts at root, outside the modal surface. */
  onOpenShortcutsDialog: () => void;
}

const REDUCE_MOTION_LABELS: Record<ReduceMotionPreference, string> = {
  system: 'Match system',
  always: 'Always',
};

/**
 * The handful of shortcuts surfaced inline in this section. Rendered
 * from the central registry (`keyboardShortcuts.ts`) so the keycaps and
 * labels can never drift from the authoritative definitions.
 */
const SHORTCUT_ROWS: { id: ShortcutId; label: string }[] = [
  { id: 'commandPalette', label: 'Open command palette' },
  { id: 'openSettings', label: 'Open settings' },
  { id: 'shortcutsHelp', label: 'Keyboard shortcuts' },
];

/**
 * Accessibility preferences. Reduce motion lives here — every major
 * operating system surfaces it under Accessibility, so this is where
 * users look for it. The Appearance section intentionally does not
 * duplicate it.
 */
export const AccessibilitySection = memo<AccessibilitySectionProps>(({ onOpenShortcutsDialog }) => {
  const styles = useSettingCardStyles();
  const modalStyles = useSettingsModalStyles();
  // Subscribe per-field — see AppearanceSection for rationale.
  const reduceMotion = useAppStore((s) => s.reduceMotion);
  const setReduceMotion = useAppStore((s) => s.setReduceMotion);

  return (
    <div className={styles.list}>
      <div className={styles.listRow}>
        <div>
          <p className={styles.cardLabel}>Reduce motion</p>
          <p className={styles.cardHelp}>
            Disables non-essential animations across the portal. “Match system” honors your OS-level{' '}
            <Text wrap={false} className={styles.mono}>prefers-reduced-motion</Text> setting.
          </p>
        </div>
        <div className={styles.cardControl}>
          <Dropdown
            aria-label="Reduce motion"
            value={REDUCE_MOTION_LABELS[reduceMotion]}
            selectedOptions={[reduceMotion]}
            inlinePopup
            positioning="below-start"
            onOptionSelect={(_, data) => {
              const next = (data.optionValue ?? 'system') as ReduceMotionPreference;
              setReduceMotion(next);
            }}
          >
            <Option value="system">Match system</Option>
            <Option value="always">Always</Option>
          </Dropdown>
        </div>
      </div>

      <div className={styles.listRow}>
        <div>
          <p className={styles.cardLabel}>Keyboard shortcuts</p>
          <p className={styles.cardHelp}>
            Press <kbd className={modalStyles.kbd}>?</kbd> anywhere in Auto to open the full reference dialog.
          </p>
        </div>
        <div className={styles.cardControl}>
          <Button appearance="secondary" size="small" onClick={onOpenShortcutsDialog}>
            View shortcuts
          </Button>
        </div>
        <div className={styles.rowBodyWide}>
          <Table size="small" aria-label="Common keyboard shortcuts">
            <TableHeader>
              <TableRow>
                <TableHeaderCell>Action</TableHeaderCell>
                <TableHeaderCell className={styles.shortcutsKeyCell}>Shortcut</TableHeaderCell>
              </TableRow>
            </TableHeader>
            <TableBody>
              {SHORTCUT_ROWS.map(({ id, label }) => (
                <TableRow key={id}>
                  <TableCell>{label}</TableCell>
                  <TableCell className={styles.shortcutsKeyCell}>
                    {getShortcutParts(id).map((part, i) => (
                      <kbd key={i} className={modalStyles.kbd}>{part}</kbd>
                    ))}
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </div>
      </div>
    </div>
  );
});
AccessibilitySection.displayName = 'AccessibilitySection';
